import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal from "@react/shared/ui/Modal"
import Badge from "@react/shared/ui/Badge"
import { fmt, dateFmt } from "../helpers/format"
import { fetchPurchaseInvoiceShow } from "../api/purchase_invoices"

const STATUS_TONE = { Paid: "emerald", Confirmed: "blue", Pending: "amber" }
const ts = (key) => I18n.t(`projects.expenses.invoices.show.${key}`)
const tf = (key) => I18n.t(`projects.expenses.invoices.form.${key}`)

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
    <div className="text-[13px] text-slate-800 mt-0.5">{children || "—"}</div>
  </div>
)

const PurchaseInvoiceShowModal = ({ id, isOpen, onClose }) => {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !id) return
    setLoading(true); setData(null)
    fetchPurchaseInvoiceShow(id).then(setData).finally(() => setLoading(false))
  }, [isOpen, id])

  const footer = data && !loading && (
    <>
      <a href={data.show_path} className="text-[13px] text-slate-500 hover:text-slate-800 font-medium">
        <FontAwesomeIcon icon="arrow-up-right-from-square" className="mr-1.5" />{ts("go_to_card")}
      </a>
      <div className="flex items-center gap-2">
        {data.document_url && (
          <a href={data.document_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[13px] font-medium text-slate-700 border border-slate-300 hover:bg-slate-50">
            <FontAwesomeIcon icon="file-pdf" /> {ts("preview_pdf")}
          </a>
        )}
      </div>
    </>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} eyebrow={ts("eyebrow")} title={data?.title || ""} size="lg" footer={footer}>
      {loading || !data ? (
        <div className="py-16 text-center text-slate-400"><FontAwesomeIcon icon="circle-notch" spin className="text-2xl" /></div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Badge tone={STATUS_TONE[data.status] || "neutral"} variant="soft" size="sm">{data.status_label}</Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label={ts("supplier")}>{data.supplier?.name}</Field>
            <Field label={ts("issue_date")}>{data.issue_date ? dateFmt(data.issue_date) : "—"}</Field>
            <Field label={ts("due_date")}>{data.due_date ? dateFmt(data.due_date) : "—"}</Field>
            {data.project && (
              <Field label={ts("project")}>
                <a href={data.project.path} className="text-brand-600 hover:underline">{data.project.name}</a>
              </Field>
            )}
            {data.purchase_order && (
              <Field label={ts("purchase_order")}>
                <a href={data.purchase_order.path} className="text-brand-600 hover:underline">{data.purchase_order.name}</a>
              </Field>
            )}
            <Field label={ts("description")}>{data.description}</Field>
          </div>

          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_90px_minmax(0,1fr)] gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">
              <div>{tf("line_description")}</div>
              <div className="text-right">{tf("subtotal")}</div>
              <div className="text-center">{tf("vat")}</div>
              <div className="text-right">{tf("row_total")}</div>
            </div>
            <div className="divide-y divide-slate-100">
              {data.lines.map((row, index) => (
                <div key={index} className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_90px_minmax(0,1fr)] gap-3 px-4 py-2 items-center text-[12.5px]">
                  <div className="tw-rich-text text-slate-700 min-w-0" dangerouslySetInnerHTML={{ __html: row.description || "—" }} />
                  <div className="text-right tabular-nums text-slate-700">{fmt(row.subtotal)}</div>
                  <div className="text-center text-slate-500">{row.vat != null ? `${Math.round(row.vat)}%` : "—"}</div>
                  <div className="text-right tabular-nums text-slate-900 font-medium">{fmt(row.total)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <div className="w-full md:w-72 rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-1.5">
              <div className="flex items-center justify-between text-[13px]"><span className="text-slate-500">{tf("subtotal_ht")}</span><span className="tabular-nums font-medium text-slate-900">{fmt(data.subtotal)}</span></div>
              <div className="flex items-center justify-between text-[12.5px]"><span className="text-slate-400">{tf("vat")}</span><span className="tabular-nums text-slate-600">{fmt(data.vat)}</span></div>
              <div className="flex items-center justify-between text-[15px] pt-1.5 border-t border-slate-200"><span className="font-semibold text-slate-700">{tf("total_ttc")}</span><span className="tabular-nums font-semibold text-slate-900">{fmt(data.total)}</span></div>
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}

export default PurchaseInvoiceShowModal
